import { cloneDeep } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Button, SliderInput } from '@meitu/saas-ui';
import { Editor } from '@meitu/saas-editor-core/es/index';
import { ImageLayer } from '@meitu/saas-editor-core/es/layers/image-layer';
import ImageEffectAction from '@meitu/saas-editor-core/es/layers/image-layer/utils/image-effect-action';
import { IImageLayerProps, ILayerProps } from '@meitu/saas-editor-core/es/version-types';
import IconFont from '@meitu/xdesign-icon-font';
import { useLocaleStore } from '@/i18n/index';
import classicImg from '../assets/blurEffect.jpg';
import glassImg from '../assets/glassEffect.jpg';

enum BlurType {
    none,
    classic,
    glass,
}
const ImageBlur: React.FC<{
    setPanel: Function;
    editor: Editor;
}> = ({ setPanel, editor }) => {
    const { $t } = useLocaleStore();

    const [originLayer, setOriginLayer] = useState<ILayerProps>();

    const [blurType, setBlurType] = useState(BlurType.none);

    const [alpha, setAlpha] = useState(50);

    const [activeLayer, setActiveLayer] = useState<ILayerProps | null>(editor.active.getPick(true));

    useEffect(() => {
        return () => {
            editor.getInstance(ImageLayer).resetClassicBlurParams();
        };
    }, []);

    useEffect(() => {
        const eventId = editor.active.onChange((layer, pickLayer) => {
            if (pickLayer) {
                setActiveLayer({ ...pickLayer });
            } else if (layer) {
                setActiveLayer({ ...layer });
            } else {
                setActiveLayer(null);
            }
        });

        return () => {
            editor.off(eventId);
        };
    }, []);
    useEffect(() => {
        if (!activeLayer || originLayer !== undefined) {
            return;
        }

        const oldLayer = cloneDeep(activeLayer);
        oldLayer.__renderEnhanceCanvas = false;
        oldLayer.__renderGLCanvas = false;
        setOriginLayer(oldLayer);
    }, [activeLayer]);

    const cancelBlurClick = () => {
        if (!originLayer) {
            return;
        }

        editor.getInstance(ImageLayer).updateLayer(originLayer, undefined, false);
        setBlurType(BlurType.none);
        window._stat_track?.('hb_layers_func_click', {
            func_id: '2_108',
        });
    };

    const classicBlurClick = () => {
        setBlurType(BlurType.classic);
        if (!originLayer) {
            return;
        }

        editor.getInstance(ImageLayer).renderClassicBlur(cloneDeep(originLayer), alpha);
        window._stat_track?.('hb_layers_func_click', {
            func_id: '2_108',
        });
    };

    const glassBlurClick = async () => {
        setBlurType(BlurType.glass);
        if (!originLayer) {
            return;
        }
        await editor.getInstance(ImageLayer).renderImageGlassBlur(cloneDeep(originLayer));
        ImageEffectAction.changeImageGlassBlurAlpha(alpha);
        window._stat_track?.('hb_layers_func_click', {
            func_id: '2_108',
        });
    };

    const classicBlurAlphaChange = (val: number) => {
        setAlpha(val);
        editor.getInstance(ImageLayer).changeClassicBlurAlpha(val);
    };

    const glassBlurAlphaChange = (val: number) => {
        setAlpha(val);
        ImageEffectAction.changeImageGlassBlurAlpha(val);
    };

    const applyClick = async () => {
        if (!originLayer || !activeLayer) {
            return;
        }

        let resUrl = '';

        if (blurType === BlurType.classic) {
            resUrl = await ImageEffectAction.getImageClassicBlurResultUrl();
        } else {
            resUrl = await ImageEffectAction.getImageGlassBlurResultUrl();
        }

        const newLayer = cloneDeep(activeLayer);
        if (resUrl) {
            (newLayer.data as IImageLayerProps).url = resUrl;
        }

        newLayer.__renderEnhanceCanvas = false;
        newLayer.__renderGLCanvas = false;
        newLayer.__renderEnhanceCanvas = false;
        newLayer.__renderGLCanvas = false;

        editor
            .getInstance(ImageLayer)
            .updateLayer(
                { ...newLayer, __renderEnhanceCanvas: false, __renderGLCanvas: false },
                { ...activeLayer, __renderEnhanceCanvas: false, __renderGLCanvas: false },
                true,
            );
        setOriginLayer(cloneDeep(newLayer));
        setPanel(null);
        window._stat_track?.('hb_layers_func_click', {
            func_id: '2_108',
        });
        window._stat_track?.('hb_layers_func_use', {
            func_id: '2_108',
        });
    };

    return (
        <div className="c-image-effect-blur-content">
            <div className="effect-list">
                <div className={`item ${blurType === BlurType.none ? 'active' : ''}`} onClick={cancelBlurClick}>
                    <div className="none">
                        <IconFont name="SlashCircleBold" fontSize={28} color="rgba(171, 173, 178, 1)" />
                    </div>
                    <span className="name">{$t((v) => v.imageEffectPanel.imageBlur.cancelBlur)}</span>
                </div>

                <div
                    className={`item ${blurType === BlurType.classic ? 'active' : ''}`}
                    onClick={() => {
                        classicBlurClick();
                    }}
                >
                    <img src={`${classicImg}`} alt="图片" />
                    <span className="name">{$t((v) => v.imageEffectPanel.imageBlur.classic)}</span>
                </div>

                <div
                    className={`item ${blurType === BlurType.glass ? 'active' : ''}`}
                    onClick={() => {
                        glassBlurClick();
                    }}
                >
                    <img src={`${glassImg}`} alt="图片" />
                    <span className="name">{$t((v) => v.imageEffectPanel.imageBlur.glass)}</span>
                </div>
            </div>

            <div className={`${blurType !== BlurType.none ? '' : 'disable'} progress`}>
                <span className="text">{$t((v) => v.imageEffectPanel.imageBlur.strength)}</span>
                <SliderInput
                    min={1}
                    max={100}
                    value={alpha}
                    precision={1}
                    showStepBtn
                    type="horizontal"
                    onChange={(val: number) => {
                        if (blurType === BlurType.classic) {
                            classicBlurAlphaChange(val);
                        } else {
                            glassBlurAlphaChange(val);
                        }
                    }}
                    onAfterChange={(val: number) => {
                        if (blurType === BlurType.classic) {
                            classicBlurAlphaChange(val);
                        } else {
                            glassBlurAlphaChange(val);
                        }
                        window._stat_track?.('hb_layers_func_click', {
                            func_id: '2_108',
                        });
                    }}
                    onClick={() => {}}
                />
            </div>

            <div className="operate">
                <Button
                    type="ghost"
                    onClick={() => {
                        cancelBlurClick();
                        setPanel(null);
                    }}
                >
                    {$t((v) => v.imageEffectPanel.imageBlur.cancel)}
                </Button>
                <Button
                    className="done"
                    onClick={() => {
                        if (blurType === BlurType.none) {
                            setPanel(null);
                            return;
                        }
                        applyClick();
                    }}
                >
                    {$t((v) => v.imageEffectPanel.imageBlur.apply)}
                </Button>
            </div>
        </div>
    );
};

export default ImageBlur;
